import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import { storage } from 'utils/storage';
import { TCustomerSettings } from 'types/customer';
import { createLocalTheme } from 'config/theme';
import { useAuth } from './authContext';

interface ThemeContextProps {
  setTheme: (theme: 'light' | 'dark' | 'time') => void;
}
const ThemeContext = createContext({} as ThemeContextProps);
type Theme =
  | 'dark'
  | 'light'
  | 'time'
  | Omit<string, 'dark' | 'light' | 'time'>;

const LocalThemeProvider = ({ children }: { children: ReactNode }) => {
  const { updateCustomerSettings, user } = useAuth();

  const [appTheme, setAppTheme] = useState<Theme>(() => {
    const localValues = storage.getItem({
      key: 'settings',
      storageType: 'local',
    }) as TCustomerSettings;

    if (localValues) {
      const { theme } = { theme: 'time' };
      if (theme === 'time')
        return new Date().getHours() < 18 ? 'light' : 'dark';
      return theme ?? 'dark';
    }

    return 'dark';
  });

  const theme = createLocalTheme(appTheme as 'light' | 'dark');

  const setTheme = useCallback(
    (value: 'dark' | 'light' | 'time') => {
      setAppTheme(value);
      updateCustomerSettings({ theme: value });
    },
    [updateCustomerSettings],
  );

  useEffect(() => {
    if (user.settings?.theme) setAppTheme(user.settings?.theme);
  }, [user.settings?.theme]);

  return (
    <ThemeContext.Provider value={{ setTheme }}>
      <ThemeProvider theme={{ ...theme }}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

function useLocalTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within an AuthPovider');
  }

  return context;
}

export { useLocalTheme, LocalThemeProvider };
